import { subcategories as catalogSubcategories } from "@/lib/categoryStructure";
import { parseDimensionsFtText } from "@/lib/listing/dimensionsFtText";
import {
  CHAIR_MATERIALS,
  CHAIR_QUANTITY_MODES,
  SOFA_SHAPES,
  validateStoredChairAttributes,
  isChairLikeSubcategory,
} from "@/lib/listing/chairListingSpec";
import {
  DINING_CHAIR_COUNTS,
  DINING_TABLE_TOP_MATERIALS,
  isDiningTableChairSubcategory,
  validateStoredDiningTableChairAttributes,
} from "@/lib/listing/diningTableChairListingSpec";
import {
  FURNITURE_MATERIALS,
  HEIGHT_FT_MAX,
  HEIGHT_FT_MIN,
  LENGTH_FT_MAX,
  LENGTH_FT_MIN,
  WIDTH_FT_MAX,
  WIDTH_FT_MIN,
  isFurnitureSurfaceSubcategory,
  validateStoredFurnitureSurfaceAttributes,
} from "@/lib/listing/furnitureSurfaceListingSpec";
import {
  OFFICE_TABLE_CHAIR_COUNTS,
  TABLE_CHAIR_COUNTS,
  isFurnitureTableBundleSubcategory,
  validateStoredFurnitureTableBundleAttributes,
} from "@/lib/listing/furnitureTableBundleListingSpec";
import {
  AC_HORSE_POWER_MAX,
  AC_HORSE_POWER_MIN,
  isAcSubcategory,
  validateStoredAcAttributes,
} from "@/lib/listing/acListingSpec";
import { APPLIANCE_INVERTER_TYPES } from "@/lib/listing/applianceInverterListingSpec";
import {
  MICROWAVE_SMART_TYPES,
  isMicrowaveSubcategory,
  validateStoredMicrowaveAttributes,
} from "@/lib/listing/microwaveListingSpec";
import {
  FAN_BLADE_SIZE_INCHES_MAX,
  FAN_BLADE_SIZE_INCHES_MIN,
  FAN_MOUNT_TYPES,
  FAN_POWER_SOURCES,
  isFanSubcategory,
  validateStoredFanAttributes,
} from "@/lib/listing/fanListingSpec";
import {
  SEWING_MACHINE_TYPES,
  validateStoredSewingMachineAttributes,
} from "@/lib/listing/sewingMachineListingSpec";
import {
  PLAY_STATION_EDITIONS,
  PLAY_STATION_GENERATIONS,
  PLAY_STATION_GAMES_COUNT_MAX,
  PLAY_STATION_GAMES_COUNT_MIN,
  PLAY_STATION_PAD_COUNT_MAX,
  PLAY_STATION_PAD_COUNT_MIN,
  validateStoredPlayStationAttributes,
} from "@/lib/listing/playStationListingSpec";
import { isListingFamilyMember } from "@/lib/listing/listingFamilyRegistry";
import {
  GAS_COOKER_BURNER_COUNT_MAX,
  GAS_COOKER_BURNER_COUNT_MIN,
  GAS_COOKER_MOUNT_TYPES,
  isGasCookerSubcategory,
  validateStoredGasCookerAttributes,
} from "@/lib/listing/gasCookerListingSpec";
import {
  FRIDGE_CAPACITY_LITERS_MINI_MAX,
  FRIDGE_CAPACITY_LITERS_MINI_MIN,
  FRIDGE_CAPACITY_LITERS_FULL_MAX,
  FRIDGE_CAPACITY_LITERS_FULL_MIN,
  FRIDGE_DOOR_TYPES,
  FRIDGE_SMART_TYPES,
  isFridgeSubcategory,
  validateStoredFridgeAttributes,
} from "@/lib/listing/fridgeListingSpec";
import {
  KITCHEN_CABINET_MOUNT_TYPES,
  isKitchenCabinetSubcategory,
  validateStoredKitchenCabinetAttributes,
} from "@/lib/listing/kitchenCabinetListingSpec";
import {
  WASHER_LOADER_TYPES,
  WASHING_MACHINE_LOAD_KG_MAX,
  WASHING_MACHINE_LOAD_KG_MIN,
  isWashingMachineSubcategory,
  validateStoredWashingMachineAttributes,
} from "@/lib/listing/washingMachineListingSpec";
import { LISTING_SPEC_ENABLED_WAVES } from "@/lib/listing/listingSpecEnforcementConfig";
export { LISTING_SPEC_ENABLED_WAVES };
export const LISTING_SPEC_VERSION = 1 as const;

export type ListingSpecKey =
  | "capacity_kva"
  | "horse_power"
  | "capacity_liters"
  | "capacity_kg"
  | "screen_inches"
  | "dimensions_ft"
  | "mattress_size"
  | "ram_gb"
  | "storage_gb"
  | "burner_count"
  | "load_capacity_kg"
  | "washer_loader_type"
  | "appliance_inverter_type"
  | "microwave_smart_type"
  | "fridge_smart_type"
  | "fridge_door_type"
  | "ac_remote_included"
  | "ac_installation_kit_included"
  | "fan_mount_type"
  | "fan_power_source"
  | "blade_size_inches"
  | "sewing_machine_type"
  | "play_station_generation"
  | "play_station_edition"
  | "play_station_pad_count"
  | "play_station_games_count"
  | "gas_cooker_mount_type"
  | "gas_cylinder_included"
  | "kitchen_cabinet_mount_type"
  | "chair_quantity_mode"
  | "chair_seat_capacities"
  | "chair_material"
  | "sofa_shape"
  | "dining_chair_count"
  | "dining_table_top_material"
  | "table_top_material"
  | "chair_count"
  | "furniture_material"
  | "length_ft"
  | "width_ft"
  | "height_ft";

export type ListingSpecWave = "wave_1" | "wave_2" | "wave_3";

export type ListingSpecInputType =
  | "number"
  | "integer"
  | "enum"
  | "boolean"
  | "dimensions_ft"
  | "integer_array";

export type DimensionsFtValue = {
  length: number;
  width: number;
  height?: number;
};

export type ListingSpecValue =
  | number
  | string
  | boolean
  | number[]
  | DimensionsFtValue
  | null
  | undefined;

/**
 * Keys the generic size-line can derive. Registry enforcement for live AC/washer
 * stays on these until Phase 2 plugin UI owns the full appliance required set.
 */
export const SIZE_LINE_DERIVABLE_SPEC_KEYS: readonly ListingSpecKey[] = [
  "capacity_kva",
  "horse_power",
  "capacity_liters",
  "capacity_kg",
  "screen_inches",
  "dimensions_ft",
  "mattress_size",
  "ram_gb",
  "storage_gb",
  "burner_count",
  "load_capacity_kg",
] as const;

export type ListingSpecAttributes = Partial<
  Record<ListingSpecKey, ListingSpecValue>
>;

export interface ListingSpecUiMeta {
  label: string;
  hint?: string;
  unit?: string;
  inputType: ListingSpecInputType;
  placeholder?: string;
}

export interface ListingSpecConstraint {
  min?: number;
  max?: number;
  integer?: boolean;
  enumValues?: readonly string[];
}

export interface ListingSpecMessageSet {
  required: string;
  invalidType?: string;
  outOfRange?: string;
  invalidEnum?: string;
}

export interface SubcategorySpecRequirement {
  subcategoryId: string;
  wave: ListingSpecWave;
  required: readonly ListingSpecKey[];
  optional: readonly ListingSpecKey[];
  constraints: Partial<Record<ListingSpecKey, ListingSpecConstraint>>;
  ui: Partial<Record<ListingSpecKey, ListingSpecUiMeta>>;
  messages: Partial<Record<ListingSpecKey, ListingSpecMessageSet>>;
}

export interface ListingSpecValidationIssue {
  key: ListingSpecKey | "__unknown__";
  code:
    | "required"
    | "invalid_type"
    | "out_of_range"
    | "invalid_enum"
    | "invalid_dimensions"
    | "unknown_key";
  message: string;
}

export interface ListingSpecValidationResult {
  isValid: boolean;
  issues: ListingSpecValidationIssue[];
  errorsByKey: Partial<Record<ListingSpecKey | "__unknown__", string[]>>;
}

export interface ListingSpecValidationOptions {
  rejectUnknownKeys?: boolean;
  enforceRequired?: boolean;
  /**
   * When set, only these required keys are enforced (size-line compat until
   * appliance plugin UI owns the full required set in Phase 2).
   */
  enforceRequiredKeys?: readonly ListingSpecKey[];
}

export interface ListingSpecInputDraft {
  subcategoryId: string | null | undefined;
  sizeText?: string | null;
}

export interface WaveEnforcementStatus {
  subcategoryId: string;
  isActive: boolean;
  hasRequirementProfile: boolean;
  wave: ListingSpecWave | null;
  shouldEnforce: boolean;
  reason:
    | "missing_subcategory"
    | "inactive_subcategory"
    | "no_requirement_profile"
    | "wave_not_enabled"
    | "enforced";
}

const specUiMeta: Record<ListingSpecKey, ListingSpecUiMeta> = {
  capacity_kva: { label: "Capacity", unit: "KVA", inputType: "number" },
  horse_power: { label: "Horse Power", unit: "HP", inputType: "number" },
  capacity_liters: { label: "Capacity", unit: "L", inputType: "number" },
  capacity_kg: { label: "Capacity", unit: "kg", inputType: "number" },
  screen_inches: { label: "Screen Size", unit: "inches", inputType: "number" },
  dimensions_ft: {
    label: "Dimensions",
    unit: "ft",
    inputType: "dimensions_ft",
    placeholder: "length x width (height optional)",
  },
  mattress_size: {
    label: "Mattress Size",
    inputType: "enum",
    placeholder: "single, double, queen, king, super_king",
  },
  ram_gb: { label: "RAM", unit: "GB", inputType: "integer" },
  storage_gb: { label: "Storage", unit: "GB", inputType: "integer" },
  burner_count: { label: "Burner Count", inputType: "integer" },
  load_capacity_kg: { label: "Load Capacity", unit: "kg", inputType: "number" },
  washer_loader_type: {
    label: "Loader type",
    inputType: "enum",
    placeholder: "top_loader or front_loader",
  },
  appliance_inverter_type: {
    label: "Inverter type",
    inputType: "enum",
    placeholder: "inverter or non_inverter",
  },
  microwave_smart_type: {
    label: "Smart type",
    inputType: "enum",
    placeholder: "smart or non_smart",
  },
  fridge_smart_type: {
    label: "Smart features",
    inputType: "enum",
    placeholder: "smart or non_smart",
  },
  fridge_door_type: {
    label: "Door type",
    inputType: "enum",
    placeholder: "side_by_side, top_bottom, single, mini",
  },
  ac_remote_included: {
    label: "Remote included",
    inputType: "boolean",
  },
  ac_installation_kit_included: {
    label: "Installation kit included",
    inputType: "boolean",
  },
  fan_mount_type: {
    label: "Mount type",
    inputType: "enum",
    placeholder: "standing, ceiling, or wall",
  },
  fan_power_source: {
    label: "Power source",
    inputType: "enum",
    placeholder: "rechargeable or non_rechargeable",
  },
  blade_size_inches: {
    label: "Blade size",
    unit: "inches",
    inputType: "number",
    placeholder: "optional",
  },
  sewing_machine_type: {
    label: "Sewing machine type",
    inputType: "enum",
    placeholder: "manual, electric, or industrial",
  },
  play_station_generation: {
    label: "PlayStation generation",
    inputType: "enum",
    placeholder: "ps2, ps3, ps4, or ps5",
  },
  play_station_edition: {
    label: "PlayStation edition",
    inputType: "enum",
    placeholder: "slim, pro, fat, or digital",
  },
  play_station_pad_count: {
    label: "Number of pads",
    inputType: "integer",
    placeholder: "optional",
  },
  play_station_games_count: {
    label: "Available games",
    inputType: "integer",
    placeholder: "optional",
  },
  gas_cooker_mount_type: {
    label: "Mount type",
    inputType: "enum",
    placeholder: "standing or table_top",
  },
  gas_cylinder_included: {
    label: "Includes gas cylinder",
    inputType: "boolean",
  },
  kitchen_cabinet_mount_type: {
    label: "Mount type",
    inputType: "enum",
    placeholder: "floor or hanging",
  },
  chair_quantity_mode: {
    label: "Quantity",
    inputType: "enum",
    placeholder: "single or set",
  },
  chair_seat_capacities: {
    label: "Seating capacity",
    inputType: "integer_array",
    placeholder: "1–8 per chair",
  },
  chair_material: {
    label: "Material",
    inputType: "enum",
    placeholder: "leather, suede, plastic, mat, other",
  },
  sofa_shape: {
    label: "Sofa shape",
    inputType: "enum",
    placeholder: "standard or L-shaped",
  },
  dining_chair_count: {
    label: "Chair count",
    inputType: "integer",
    placeholder: "3, 4, 5, or 6",
  },
  dining_table_top_material: {
    label: "Table top material",
    inputType: "enum",
    placeholder: "glass, marble, wood, granite, other",
  },
  table_top_material: {
    label: "Table top material",
    inputType: "enum",
    placeholder: "glass, marble, wood, granite, other",
  },
  chair_count: {
    label: "Chair count",
    inputType: "integer",
    placeholder: "1–6 depending on subcategory",
  },
  furniture_material: {
    label: "Material",
    inputType: "enum",
    placeholder: "wood, mdf, metal, glass, other",
  },
  length_ft: {
    label: "Length",
    unit: "ft",
    inputType: "number",
    placeholder: "optional",
  },
  width_ft: {
    label: "Width",
    unit: "ft",
    inputType: "number",
    placeholder: "optional",
  },
  height_ft: {
    label: "Height",
    unit: "ft",
    inputType: "number",
    placeholder: "optional",
  },
};

const DEFAULT_MESSAGES: ListingSpecMessageSet = {
  required: "This field is required.",
  invalidType: "Please enter a valid value.",
  outOfRange: "Value is out of allowed range.",
  invalidEnum: "Please choose one of the allowed options.",
};

function messages(required: string): ListingSpecMessageSet {
  return {
    ...DEFAULT_MESSAGES,
    required,
  };
}

function wave1Requirement(
  subcategoryId: string,
  required: readonly ListingSpecKey[],
  optional: readonly ListingSpecKey[],
  constraints: Partial<Record<ListingSpecKey, ListingSpecConstraint>>,
  requiredMessages: Partial<Record<ListingSpecKey, string>>,
): SubcategorySpecRequirement {
  const ui: Partial<Record<ListingSpecKey, ListingSpecUiMeta>> = {};
  const msg: Partial<Record<ListingSpecKey, ListingSpecMessageSet>> = {};
  for (const key of [...required, ...optional]) {
    ui[key] = specUiMeta[key];
    msg[key] = messages(
      requiredMessages[key] ?? `${specUiMeta[key].label} is required.`,
    );
  }
  return {
    subcategoryId,
    wave: "wave_1",
    required,
    optional,
    constraints,
    ui,
    messages: msg,
  };
}

export const subcategorySpecRequirements: Record<
  string,
  SubcategorySpecRequirement
> = {
  ...Object.fromEntries(
    catalogSubcategories.map((sub) => [
      sub.id,
      wave1Requirement(sub.id, [], [], {}, {}),
    ]),
  ),
  generator: wave1Requirement(
    "generator",
    ["capacity_kva"],
    [],
    { capacity_kva: { min: 0.5, max: 2500 } },
    { capacity_kva: "Generator capacity (KVA) is required." },
  ),
  "air-conditioner": wave1Requirement(
    "air-conditioner",
    ["horse_power", "appliance_inverter_type"],
    ["ac_remote_included", "ac_installation_kit_included"],
    {
      horse_power: { min: AC_HORSE_POWER_MIN, max: AC_HORSE_POWER_MAX },
      appliance_inverter_type: { enumValues: APPLIANCE_INVERTER_TYPES },
    },
    {
      horse_power: "AC horse power (HP) is required.",
      appliance_inverter_type: "Choose inverter or non-inverter.",
    },
  ),
  "inverter-air-conditioner": wave1Requirement(
    "inverter-air-conditioner",
    ["horse_power", "appliance_inverter_type"],
    ["ac_remote_included", "ac_installation_kit_included"],
    {
      horse_power: { min: AC_HORSE_POWER_MIN, max: AC_HORSE_POWER_MAX },
      appliance_inverter_type: { enumValues: APPLIANCE_INVERTER_TYPES },
    },
    {
      horse_power: "Inverter AC horse power (HP) is required.",
      appliance_inverter_type: "Choose inverter or non-inverter.",
    },
  ),
  "washing-machine": wave1Requirement(
    "washing-machine",
    ["load_capacity_kg", "appliance_inverter_type", "washer_loader_type"],
    [],
    {
      load_capacity_kg: {
        min: WASHING_MACHINE_LOAD_KG_MIN,
        max: WASHING_MACHINE_LOAD_KG_MAX,
      },
      appliance_inverter_type: { enumValues: APPLIANCE_INVERTER_TYPES },
      washer_loader_type: { enumValues: WASHER_LOADER_TYPES },
    },
    {
      load_capacity_kg: "Load capacity (kg) is required.",
      appliance_inverter_type: "Choose inverter or non-inverter.",
      washer_loader_type: "Choose top load or front load.",
    },
  ),
  "inverter-washing-machine": wave1Requirement(
    "inverter-washing-machine",
    ["load_capacity_kg", "appliance_inverter_type", "washer_loader_type"],
    [],
    {
      load_capacity_kg: {
        min: WASHING_MACHINE_LOAD_KG_MIN,
        max: WASHING_MACHINE_LOAD_KG_MAX,
      },
      appliance_inverter_type: { enumValues: APPLIANCE_INVERTER_TYPES },
      washer_loader_type: { enumValues: WASHER_LOADER_TYPES },
    },
    {
      load_capacity_kg: "Load capacity (kg) is required.",
      appliance_inverter_type: "Choose inverter or non-inverter.",
      washer_loader_type: "Choose top load or front load.",
    },
  ),
  microwave: wave1Requirement(
    "microwave",
    ["appliance_inverter_type", "microwave_smart_type"],
    [],
    {
      appliance_inverter_type: { enumValues: APPLIANCE_INVERTER_TYPES },
      microwave_smart_type: { enumValues: MICROWAVE_SMART_TYPES },
    },
    {
      appliance_inverter_type: "Choose inverter or non-inverter.",
      microwave_smart_type: "Choose smart or non-smart.",
    },
  ),
  "inverter-microwave": wave1Requirement(
    "inverter-microwave",
    ["appliance_inverter_type", "microwave_smart_type"],
    [],
    {
      appliance_inverter_type: { enumValues: APPLIANCE_INVERTER_TYPES },
      microwave_smart_type: { enumValues: MICROWAVE_SMART_TYPES },
    },
    {
      appliance_inverter_type: "Choose inverter or non-inverter.",
      microwave_smart_type: "Choose smart or non-smart.",
    },
  ),
  "non-inverter-microwave": wave1Requirement(
    "non-inverter-microwave",
    ["appliance_inverter_type", "microwave_smart_type"],
    [],
    {
      appliance_inverter_type: { enumValues: APPLIANCE_INVERTER_TYPES },
      microwave_smart_type: { enumValues: MICROWAVE_SMART_TYPES },
    },
    {
      appliance_inverter_type: "Choose inverter or non-inverter.",
      microwave_smart_type: "Choose smart or non-smart.",
    },
  ),
  "smart-microwave": wave1Requirement(
    "smart-microwave",
    ["appliance_inverter_type", "microwave_smart_type"],
    [],
    {
      appliance_inverter_type: { enumValues: APPLIANCE_INVERTER_TYPES },
      microwave_smart_type: { enumValues: MICROWAVE_SMART_TYPES },
    },
    {
      appliance_inverter_type: "Choose inverter or non-inverter.",
      microwave_smart_type: "Choose smart or non-smart.",
    },
  ),
  "non-smart-microwave": wave1Requirement(
    "non-smart-microwave",
    ["appliance_inverter_type", "microwave_smart_type"],
    [],
    {
      appliance_inverter_type: { enumValues: APPLIANCE_INVERTER_TYPES },
      microwave_smart_type: { enumValues: MICROWAVE_SMART_TYPES },
    },
    {
      appliance_inverter_type: "Choose inverter or non-inverter.",
      microwave_smart_type: "Choose smart or non-smart.",
    },
  ),
  fan: wave1Requirement(
    "fan",
    ["fan_mount_type", "fan_power_source"],
    ["blade_size_inches"],
    {
      fan_mount_type: { enumValues: FAN_MOUNT_TYPES },
      fan_power_source: { enumValues: FAN_POWER_SOURCES },
      blade_size_inches: {
        min: FAN_BLADE_SIZE_INCHES_MIN,
        max: FAN_BLADE_SIZE_INCHES_MAX,
      },
    },
    {
      fan_mount_type: "Choose how the fan is mounted.",
      fan_power_source: "Choose rechargeable or non-rechargeable.",
    },
  ),
  "standing-fan": wave1Requirement(
    "standing-fan",
    ["fan_mount_type", "fan_power_source"],
    ["blade_size_inches"],
    {
      fan_mount_type: { enumValues: FAN_MOUNT_TYPES },
      fan_power_source: { enumValues: FAN_POWER_SOURCES },
      blade_size_inches: {
        min: FAN_BLADE_SIZE_INCHES_MIN,
        max: FAN_BLADE_SIZE_INCHES_MAX,
      },
    },
    {
      fan_mount_type: "Choose how the fan is mounted.",
      fan_power_source: "Choose rechargeable or non-rechargeable.",
    },
  ),
  "wall-fan": wave1Requirement(
    "wall-fan",
    ["fan_mount_type", "fan_power_source"],
    ["blade_size_inches"],
    {
      fan_mount_type: { enumValues: FAN_MOUNT_TYPES },
      fan_power_source: { enumValues: FAN_POWER_SOURCES },
      blade_size_inches: {
        min: FAN_BLADE_SIZE_INCHES_MIN,
        max: FAN_BLADE_SIZE_INCHES_MAX,
      },
    },
    {
      fan_mount_type: "Choose how the fan is mounted.",
      fan_power_source: "Choose rechargeable or non-rechargeable.",
    },
  ),
  "ceiling-fan": wave1Requirement(
    "ceiling-fan",
    ["fan_mount_type", "fan_power_source"],
    ["blade_size_inches"],
    {
      fan_mount_type: { enumValues: FAN_MOUNT_TYPES },
      fan_power_source: { enumValues: FAN_POWER_SOURCES },
      blade_size_inches: {
        min: FAN_BLADE_SIZE_INCHES_MIN,
        max: FAN_BLADE_SIZE_INCHES_MAX,
      },
    },
    {
      fan_mount_type: "Choose how the fan is mounted.",
      fan_power_source: "Choose rechargeable or non-rechargeable.",
    },
  ),
  "rechargeable-fans": wave1Requirement(
    "rechargeable-fans",
    ["fan_mount_type", "fan_power_source"],
    ["blade_size_inches"],
    {
      fan_mount_type: { enumValues: FAN_MOUNT_TYPES },
      fan_power_source: { enumValues: FAN_POWER_SOURCES },
      blade_size_inches: {
        min: FAN_BLADE_SIZE_INCHES_MIN,
        max: FAN_BLADE_SIZE_INCHES_MAX,
      },
    },
    {
      fan_mount_type: "Choose how the fan is mounted.",
      fan_power_source: "Choose rechargeable or non-rechargeable.",
    },
  ),
  "sewing-machine": wave1Requirement(
    "sewing-machine",
    ["sewing_machine_type"],
    [],
    {
      sewing_machine_type: { enumValues: SEWING_MACHINE_TYPES },
    },
    {
      sewing_machine_type: "Choose Manual, Electric, or Industrial.",
    },
  ),
  "manual-sewing-machine": wave1Requirement(
    "manual-sewing-machine",
    ["sewing_machine_type"],
    [],
    {
      sewing_machine_type: { enumValues: SEWING_MACHINE_TYPES },
    },
    {
      sewing_machine_type: "Choose Manual, Electric, or Industrial.",
    },
  ),
  "electric-sewing-machine": wave1Requirement(
    "electric-sewing-machine",
    ["sewing_machine_type"],
    [],
    {
      sewing_machine_type: { enumValues: SEWING_MACHINE_TYPES },
    },
    {
      sewing_machine_type: "Choose Manual, Electric, or Industrial.",
    },
  ),
  "industrial-sewing-machine": wave1Requirement(
    "industrial-sewing-machine",
    ["sewing_machine_type"],
    [],
    {
      sewing_machine_type: { enumValues: SEWING_MACHINE_TYPES },
    },
    {
      sewing_machine_type: "Choose Manual, Electric, or Industrial.",
    },
  ),
  "play-station": wave1Requirement(
    "play-station",
    ["play_station_generation"],
    [
      "play_station_edition",
      "play_station_pad_count",
      "play_station_games_count",
    ],
    {
      play_station_generation: { enumValues: PLAY_STATION_GENERATIONS },
      play_station_edition: { enumValues: PLAY_STATION_EDITIONS },
      play_station_pad_count: {
        min: PLAY_STATION_PAD_COUNT_MIN,
        max: PLAY_STATION_PAD_COUNT_MAX,
        integer: true,
      },
      play_station_games_count: {
        min: PLAY_STATION_GAMES_COUNT_MIN,
        max: PLAY_STATION_GAMES_COUNT_MAX,
        integer: true,
      },
    },
    {
      play_station_generation: "Choose PS2, PS3, PS4, or PS5.",
    },
  ),
  "gaming-consoles": wave1Requirement(
    "gaming-consoles",
    ["play_station_generation"],
    [
      "play_station_edition",
      "play_station_pad_count",
      "play_station_games_count",
    ],
    {
      play_station_generation: { enumValues: PLAY_STATION_GENERATIONS },
      play_station_edition: { enumValues: PLAY_STATION_EDITIONS },
      play_station_pad_count: {
        min: PLAY_STATION_PAD_COUNT_MIN,
        max: PLAY_STATION_PAD_COUNT_MAX,
        integer: true,
      },
      play_station_games_count: {
        min: PLAY_STATION_GAMES_COUNT_MIN,
        max: PLAY_STATION_GAMES_COUNT_MAX,
        integer: true,
      },
    },
    {
      play_station_generation: "Choose PS2, PS3, PS4, or PS5.",
    },
  ),
  "gaming-console": wave1Requirement(
    "gaming-console",
    ["play_station_generation"],
    [
      "play_station_edition",
      "play_station_pad_count",
      "play_station_games_count",
    ],
    {
      play_station_generation: { enumValues: PLAY_STATION_GENERATIONS },
      play_station_edition: { enumValues: PLAY_STATION_EDITIONS },
      play_station_pad_count: {
        min: PLAY_STATION_PAD_COUNT_MIN,
        max: PLAY_STATION_PAD_COUNT_MAX,
        integer: true,
      },
      play_station_games_count: {
        min: PLAY_STATION_GAMES_COUNT_MIN,
        max: PLAY_STATION_GAMES_COUNT_MAX,
        integer: true,
      },
    },
    {
      play_station_generation: "Choose PS2, PS3, PS4, or PS5.",
    },
  ),
  "play-stations": wave1Requirement(
    "play-stations",
    ["play_station_generation"],
    [
      "play_station_edition",
      "play_station_pad_count",
      "play_station_games_count",
    ],
    {
      play_station_generation: { enumValues: PLAY_STATION_GENERATIONS },
      play_station_edition: { enumValues: PLAY_STATION_EDITIONS },
      play_station_pad_count: {
        min: PLAY_STATION_PAD_COUNT_MIN,
        max: PLAY_STATION_PAD_COUNT_MAX,
        integer: true,
      },
      play_station_games_count: {
        min: PLAY_STATION_GAMES_COUNT_MIN,
        max: PLAY_STATION_GAMES_COUNT_MAX,
        integer: true,
      },
    },
    {
      play_station_generation: "Choose PS2, PS3, PS4, or PS5.",
    },
  ),
  "gas-cooker": wave1Requirement(
    "gas-cooker",
    ["gas_cooker_mount_type", "gas_cylinder_included"],
    ["burner_count"],
    {
      gas_cooker_mount_type: { enumValues: GAS_COOKER_MOUNT_TYPES },
      burner_count: {
        min: GAS_COOKER_BURNER_COUNT_MIN,
        max: GAS_COOKER_BURNER_COUNT_MAX,
        integer: true,
      },
    },
    {
      gas_cooker_mount_type: "Choose standing or table-top.",
      gas_cylinder_included: "Say whether a gas cylinder is included.",
    },
  ),
  "standing-gas-cooker": wave1Requirement(
    "standing-gas-cooker",
    ["gas_cooker_mount_type", "gas_cylinder_included"],
    ["burner_count"],
    {
      gas_cooker_mount_type: { enumValues: GAS_COOKER_MOUNT_TYPES },
      burner_count: {
        min: GAS_COOKER_BURNER_COUNT_MIN,
        max: GAS_COOKER_BURNER_COUNT_MAX,
        integer: true,
      },
    },
    {
      gas_cooker_mount_type: "Choose standing or table-top.",
      gas_cylinder_included: "Say whether a gas cylinder is included.",
    },
  ),
  "table-top-gas-cooker": wave1Requirement(
    "table-top-gas-cooker",
    ["gas_cooker_mount_type", "gas_cylinder_included"],
    ["burner_count"],
    {
      gas_cooker_mount_type: { enumValues: GAS_COOKER_MOUNT_TYPES },
      burner_count: {
        min: GAS_COOKER_BURNER_COUNT_MIN,
        max: GAS_COOKER_BURNER_COUNT_MAX,
        integer: true,
      },
    },
    {
      gas_cooker_mount_type: "Choose standing or table-top.",
      gas_cylinder_included: "Say whether a gas cylinder is included.",
    },
  ),
  "standing-gas-cooker-cylinder": wave1Requirement(
    "standing-gas-cooker-cylinder",
    ["gas_cooker_mount_type", "gas_cylinder_included"],
    ["burner_count"],
    {
      gas_cooker_mount_type: { enumValues: GAS_COOKER_MOUNT_TYPES },
      burner_count: {
        min: GAS_COOKER_BURNER_COUNT_MIN,
        max: GAS_COOKER_BURNER_COUNT_MAX,
        integer: true,
      },
    },
    {
      gas_cooker_mount_type: "Choose standing or table-top.",
      gas_cylinder_included: "Say whether a gas cylinder is included.",
    },
  ),
  "table-top-gas-cooker-cylinder": wave1Requirement(
    "table-top-gas-cooker-cylinder",
    ["gas_cooker_mount_type", "gas_cylinder_included"],
    ["burner_count"],
    {
      gas_cooker_mount_type: { enumValues: GAS_COOKER_MOUNT_TYPES },
      burner_count: {
        min: GAS_COOKER_BURNER_COUNT_MIN,
        max: GAS_COOKER_BURNER_COUNT_MAX,
        integer: true,
      },
    },
    {
      gas_cooker_mount_type: "Choose standing or table-top.",
      gas_cylinder_included: "Say whether a gas cylinder is included.",
    },
  ),
  "smart-tv": wave1Requirement(
    "smart-tv",
    ["screen_inches"],
    [],
    { screen_inches: { min: 7, max: 120 } },
    { screen_inches: "TV screen size (inches) is required." },
  ),
  "non-smart-tv": wave1Requirement(
    "non-smart-tv",
    ["screen_inches"],
    [],
    { screen_inches: { min: 7, max: 120 } },
    { screen_inches: "TV screen size (inches) is required." },
  ),
  monitor: wave1Requirement(
    "monitor",
    ["screen_inches"],
    [],
    { screen_inches: { min: 7, max: 80 } },
    { screen_inches: "Monitor screen size (inches) is required." },
  ),
  "inverter-fridge": wave1Requirement(
    "inverter-fridge",
    ["fridge_door_type", "appliance_inverter_type", "fridge_smart_type"],
    ["capacity_liters"],
    {
      fridge_door_type: { enumValues: FRIDGE_DOOR_TYPES },
      appliance_inverter_type: { enumValues: APPLIANCE_INVERTER_TYPES },
      fridge_smart_type: { enumValues: FRIDGE_SMART_TYPES },
      capacity_liters: {
        min: FRIDGE_CAPACITY_LITERS_MINI_MIN,
        max: FRIDGE_CAPACITY_LITERS_FULL_MAX,
      },
    },
    {
      fridge_door_type: "Choose a door type.",
      capacity_liters: "Fridge capacity (liters) is required.",
      appliance_inverter_type: "Choose inverter or non-inverter.",
      fridge_smart_type: "Choose smart or non-smart.",
    },
  ),
  fridge: wave1Requirement(
    "fridge",
    ["fridge_door_type", "appliance_inverter_type", "fridge_smart_type"],
    ["capacity_liters"],
    {
      fridge_door_type: { enumValues: FRIDGE_DOOR_TYPES },
      appliance_inverter_type: { enumValues: APPLIANCE_INVERTER_TYPES },
      fridge_smart_type: { enumValues: FRIDGE_SMART_TYPES },
      capacity_liters: {
        min: FRIDGE_CAPACITY_LITERS_MINI_MIN,
        max: FRIDGE_CAPACITY_LITERS_FULL_MAX,
      },
    },
    {
      fridge_door_type: "Choose a door type.",
      capacity_liters: "Fridge capacity (liters) is required.",
      appliance_inverter_type: "Choose inverter or non-inverter.",
      fridge_smart_type: "Choose smart or non-smart.",
    },
  ),
  "standing-fridge": wave1Requirement(
    "standing-fridge",
    ["fridge_door_type", "appliance_inverter_type", "fridge_smart_type"],
    ["capacity_liters"],
    {
      fridge_door_type: { enumValues: FRIDGE_DOOR_TYPES },
      appliance_inverter_type: { enumValues: APPLIANCE_INVERTER_TYPES },
      fridge_smart_type: { enumValues: FRIDGE_SMART_TYPES },
      capacity_liters: {
        min: FRIDGE_CAPACITY_LITERS_MINI_MIN,
        max: FRIDGE_CAPACITY_LITERS_FULL_MAX,
      },
    },
    {
      fridge_door_type: "Choose a door type.",
      capacity_liters: "Fridge capacity (liters) is required.",
      appliance_inverter_type: "Choose inverter or non-inverter.",
      fridge_smart_type: "Choose smart or non-smart.",
    },
  ),
  "side-by-side-fridge": wave1Requirement(
    "side-by-side-fridge",
    ["fridge_door_type", "appliance_inverter_type", "fridge_smart_type"],
    ["capacity_liters"],
    {
      fridge_door_type: { enumValues: FRIDGE_DOOR_TYPES },
      appliance_inverter_type: { enumValues: APPLIANCE_INVERTER_TYPES },
      fridge_smart_type: { enumValues: FRIDGE_SMART_TYPES },
      capacity_liters: {
        min: FRIDGE_CAPACITY_LITERS_FULL_MIN,
        max: FRIDGE_CAPACITY_LITERS_FULL_MAX,
      },
    },
    {
      fridge_door_type: "Choose a door type.",
      capacity_liters: "Fridge capacity (liters) is required.",
      appliance_inverter_type: "Choose inverter or non-inverter.",
      fridge_smart_type: "Choose smart or non-smart.",
    },
  ),
  "mini-fridge": wave1Requirement(
    "mini-fridge",
    ["fridge_door_type", "appliance_inverter_type", "fridge_smart_type"],
    ["capacity_liters"],
    {
      fridge_door_type: { enumValues: FRIDGE_DOOR_TYPES },
      appliance_inverter_type: { enumValues: APPLIANCE_INVERTER_TYPES },
      fridge_smart_type: { enumValues: FRIDGE_SMART_TYPES },
      capacity_liters: {
        min: FRIDGE_CAPACITY_LITERS_MINI_MIN,
        max: FRIDGE_CAPACITY_LITERS_MINI_MAX,
      },
    },
    {
      fridge_door_type: "Choose a door type.",
      capacity_liters: "Capacity must be valid if provided.",
      appliance_inverter_type: "Choose inverter or non-inverter.",
      fridge_smart_type: "Choose smart or non-smart.",
    },
  ),
  "kitchen-cabinet": wave1Requirement(
    "kitchen-cabinet",
    ["kitchen_cabinet_mount_type"],
    [],
    {
      kitchen_cabinet_mount_type: {
        enumValues: KITCHEN_CABINET_MOUNT_TYPES,
      },
    },
    {
      kitchen_cabinet_mount_type: "Choose floor or hanging.",
    },
  ),
  "hanging-kitchen-cabinet": wave1Requirement(
    "hanging-kitchen-cabinet",
    ["kitchen_cabinet_mount_type"],
    [],
    {
      kitchen_cabinet_mount_type: {
        enumValues: KITCHEN_CABINET_MOUNT_TYPES,
      },
    },
    {
      kitchen_cabinet_mount_type: "Choose floor or hanging.",
    },
  ),
  "chest-freezers": wave1Requirement(
    "chest-freezers",
    ["capacity_liters"],
    [],
    { capacity_liters: { min: 20, max: 2000 } },
    { capacity_liters: "Freezer capacity (liters) is required." },
  ),
  "gas-cylinder": wave1Requirement(
    "gas-cylinder",
    ["capacity_kg"],
    [],
    { capacity_kg: { min: 1, max: 100 } },
    { capacity_kg: "Gas cylinder capacity (kg) is required." },
  ),
  mattress: wave1Requirement(
    "mattress",
    ["dimensions_ft"],
    [],
    {},
    {
      dimensions_ft:
        "Mattress length × width in feet is required (e.g. 4 x 6 or 4ft x 6ft).",
    },
  ),
  "bed-frame": wave1Requirement(
    "bed-frame",
    ["dimensions_ft"],
    [],
    {},
    { dimensions_ft: "Bed dimensions in ft are required." },
  ),
  "bed-frame-mattress": wave1Requirement(
    "bed-frame-mattress",
    ["dimensions_ft"],
    [],
    {},
    {
      dimensions_ft:
        "Bed surface length × width in feet is required (e.g. 4ft x 6ft).",
    },
  ),
  "desktop-pc": wave1Requirement(
    "desktop-pc",
    ["ram_gb", "storage_gb"],
    [],
    {
      ram_gb: { min: 1, max: 1024, integer: true },
      storage_gb: { min: 16, max: 16000, integer: true },
    },
    {
      ram_gb: "Desktop RAM (GB) is required.",
      storage_gb: "Desktop storage (GB) is required.",
    },
  ),
  "win-pc": wave1Requirement(
    "win-pc",
    ["ram_gb", "storage_gb"],
    [],
    {
      ram_gb: { min: 1, max: 1024, integer: true },
      storage_gb: { min: 16, max: 16000, integer: true },
    },
    {
      ram_gb: "PC RAM (GB) is required.",
      storage_gb: "PC storage (GB) is required.",
    },
  ),
  mac: wave1Requirement(
    "mac",
    ["ram_gb", "storage_gb"],
    [],
    {
      ram_gb: { min: 1, max: 1024, integer: true },
      storage_gb: { min: 64, max: 16000, integer: true },
    },
    {
      ram_gb: "Mac RAM (GB) is required.",
      storage_gb: "Mac storage (GB) is required.",
    },
  ),
  iphone: wave1Requirement(
    "iphone",
    ["storage_gb"],
    [],
    { storage_gb: { min: 16, max: 2048, integer: true } },
    { storage_gb: "iPhone storage (GB) is required." },
  ),
  "android-phone": wave1Requirement(
    "android-phone",
    ["storage_gb"],
    [],
    { storage_gb: { min: 16, max: 2048, integer: true } },
    { storage_gb: "Android phone storage (GB) is required." },
  ),
  ipad: wave1Requirement(
    "ipad",
    ["storage_gb"],
    [],
    { storage_gb: { min: 16, max: 4096, integer: true } },
    { storage_gb: "iPad storage (GB) is required." },
  ),
  "android-tab": wave1Requirement(
    "android-tab",
    ["storage_gb"],
    [],
    { storage_gb: { min: 16, max: 4096, integer: true } },
    { storage_gb: "Android tablet storage (GB) is required." },
  ),
  chair: wave1Requirement(
    "chair",
    ["chair_quantity_mode", "chair_seat_capacities", "chair_material"],
    [],
    {
      chair_quantity_mode: { enumValues: CHAIR_QUANTITY_MODES },
      chair_material: { enumValues: CHAIR_MATERIALS },
    },
    {
      chair_quantity_mode: "Please choose single chair or set of chairs.",
      chair_seat_capacities: "Seating capacity for each chair is required.",
      chair_material: "Chair material is required.",
    },
  ),
  "sofa-couche": wave1Requirement(
    "sofa-couche",
    ["sofa_shape", "chair_material"],
    ["chair_quantity_mode", "chair_seat_capacities"],
    {
      sofa_shape: { enumValues: SOFA_SHAPES },
      chair_quantity_mode: { enumValues: CHAIR_QUANTITY_MODES },
      chair_material: { enumValues: CHAIR_MATERIALS },
    },
    {
      sofa_shape: "Please choose standard or L-shaped sofa.",
      chair_quantity_mode: "Please choose single sofa or set of sofas.",
      chair_seat_capacities: "Seating capacity for each sofa is required.",
      chair_material: "Sofa material is required.",
    },
  ),
  /** @deprecated sell picker — prefer `sofa-couche` + `sofa_shape=l_shaped` (B1). */
  "l-shaped-chair": wave1Requirement(
    "l-shaped-chair",
    ["chair_material"],
    [],
    {
      chair_material: { enumValues: CHAIR_MATERIALS },
    },
    {
      chair_material: "Chair material is required.",
    },
  ),
  /** @deprecated sell picker — prefer `sofa-couche` + `sofa_shape=l_shaped` (B1). */
  "l-shaped-sofa": wave1Requirement(
    "l-shaped-sofa",
    ["chair_material"],
    [],
    {
      chair_material: { enumValues: CHAIR_MATERIALS },
    },
    {
      chair_material: "Sofa material is required.",
    },
  ),
  "dining-table-chair": wave1Requirement(
    "dining-table-chair",
    ["dining_chair_count", "dining_table_top_material", "chair_material"],
    [],
    {
      dining_chair_count: {
        min: DINING_CHAIR_COUNTS[0],
        max: DINING_CHAIR_COUNTS[DINING_CHAIR_COUNTS.length - 1],
        integer: true,
      },
      dining_table_top_material: { enumValues: DINING_TABLE_TOP_MATERIALS },
      chair_material: { enumValues: CHAIR_MATERIALS },
    },
    {
      dining_chair_count: "Please choose how many chairs are included (3–6).",
      dining_table_top_material: "Table top material is required.",
      chair_material: "Chair material is required.",
    },
  ),
  "table-chair": wave1Requirement(
    "table-chair",
    ["chair_count", "table_top_material", "chair_material"],
    ["length_ft", "width_ft", "dimensions_ft"],
    {
      chair_count: {
        min: TABLE_CHAIR_COUNTS[0],
        max: TABLE_CHAIR_COUNTS[TABLE_CHAIR_COUNTS.length - 1],
        integer: true,
      },
      table_top_material: { enumValues: DINING_TABLE_TOP_MATERIALS },
      chair_material: { enumValues: CHAIR_MATERIALS },
      length_ft: { min: LENGTH_FT_MIN, max: LENGTH_FT_MAX },
      width_ft: { min: WIDTH_FT_MIN, max: WIDTH_FT_MAX },
    },
    {
      chair_count: "Please choose how many chairs are included (2–6).",
      table_top_material: "Table top material is required.",
      chair_material: "Chair material is required.",
    },
  ),
  "office-table-chairs": wave1Requirement(
    "office-table-chairs",
    ["chair_count", "chair_material"],
    [
      "table_top_material",
      "length_ft",
      "width_ft",
      "height_ft",
      "dimensions_ft",
    ],
    {
      chair_count: {
        min: OFFICE_TABLE_CHAIR_COUNTS[0],
        max: OFFICE_TABLE_CHAIR_COUNTS[OFFICE_TABLE_CHAIR_COUNTS.length - 1],
        integer: true,
      },
      table_top_material: { enumValues: DINING_TABLE_TOP_MATERIALS },
      chair_material: { enumValues: CHAIR_MATERIALS },
      length_ft: { min: LENGTH_FT_MIN, max: LENGTH_FT_MAX },
      width_ft: { min: WIDTH_FT_MIN, max: WIDTH_FT_MAX },
      height_ft: { min: HEIGHT_FT_MIN, max: HEIGHT_FT_MAX },
    },
    {
      chair_count: "Please choose how many chairs are included (1–6).",
      chair_material: "Chair material is required.",
    },
  ),
  table: wave1Requirement(
    "table",
    ["table_top_material"],
    ["length_ft", "width_ft", "dimensions_ft"],
    {
      table_top_material: { enumValues: DINING_TABLE_TOP_MATERIALS },
      length_ft: { min: LENGTH_FT_MIN, max: LENGTH_FT_MAX },
      width_ft: { min: WIDTH_FT_MIN, max: WIDTH_FT_MAX },
    },
    { table_top_material: "Table top material is required." },
  ),
  "center-table": wave1Requirement(
    "center-table",
    ["table_top_material"],
    ["length_ft", "width_ft", "dimensions_ft"],
    {
      table_top_material: { enumValues: DINING_TABLE_TOP_MATERIALS },
      length_ft: { min: LENGTH_FT_MIN, max: LENGTH_FT_MAX },
      width_ft: { min: WIDTH_FT_MIN, max: WIDTH_FT_MAX },
    },
    { table_top_material: "Table top material is required." },
  ),
  "office-table": wave1Requirement(
    "office-table",
    [],
    [
      "furniture_material",
      "length_ft",
      "width_ft",
      "height_ft",
      "dimensions_ft",
    ],
    {
      furniture_material: { enumValues: FURNITURE_MATERIALS },
      length_ft: { min: LENGTH_FT_MIN, max: LENGTH_FT_MAX },
      width_ft: { min: WIDTH_FT_MIN, max: WIDTH_FT_MAX },
      height_ft: { min: HEIGHT_FT_MIN, max: HEIGHT_FT_MAX },
    },
    {},
  ),
  "tv-console": wave1Requirement(
    "tv-console",
    ["furniture_material"],
    ["length_ft", "height_ft", "dimensions_ft"],
    {
      furniture_material: { enumValues: FURNITURE_MATERIALS },
      length_ft: { min: LENGTH_FT_MIN, max: LENGTH_FT_MAX },
      height_ft: { min: HEIGHT_FT_MIN, max: HEIGHT_FT_MAX },
    },
    { furniture_material: "Material is required." },
  ),
  wardrobe: wave1Requirement(
    "wardrobe",
    ["furniture_material"],
    ["length_ft", "width_ft", "height_ft", "dimensions_ft"],
    {
      furniture_material: { enumValues: FURNITURE_MATERIALS },
      length_ft: { min: LENGTH_FT_MIN, max: LENGTH_FT_MAX },
      width_ft: { min: WIDTH_FT_MIN, max: WIDTH_FT_MAX },
      height_ft: { min: HEIGHT_FT_MIN, max: HEIGHT_FT_MAX },
    },
    { furniture_material: "Material is required." },
  ),
  dresser: wave1Requirement(
    "dresser",
    ["furniture_material"],
    ["length_ft", "width_ft", "height_ft", "dimensions_ft"],
    {
      furniture_material: { enumValues: FURNITURE_MATERIALS },
      length_ft: { min: LENGTH_FT_MIN, max: LENGTH_FT_MAX },
      width_ft: { min: WIDTH_FT_MIN, max: WIDTH_FT_MAX },
      height_ft: { min: HEIGHT_FT_MIN, max: HEIGHT_FT_MAX },
    },
    { furniture_material: "Material is required." },
  ),
};

export function getRequirementsForSubcategory(
  subcategoryId: string | null | undefined,
): SubcategorySpecRequirement | null {
  if (!subcategoryId) return null;
  return subcategorySpecRequirements[subcategoryId] ?? null;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function parseNumber(input: string): number | null {
  const n = Number.parseFloat(input);
  return Number.isFinite(n) ? n : null;
}

function matchNumber(text: string, pattern: RegExp, group = 1): number | null {
  const m = text.match(pattern);
  if (!m || !m[group]) return null;
  return parseNumber(m[group]);
}

function parseMattressSize(text: string): string | null {
  const normalized = text.toLowerCase().trim().replace(/\s+/g, "_");
  if (normalized.includes("super_king")) return "super_king";
  if (normalized.includes("king")) return "king";
  if (normalized.includes("queen")) return "queen";
  if (normalized.includes("double")) return "double";
  if (normalized.includes("single")) return "single";
  return null;
}

function parseStorageGb(text: string): number | null {
  const tb =
    matchNumber(text, /(\d+(?:\.\d+)?)\s*tb(?:\s*(?:ssd|hdd|rom|storage))?/i) ??
    null;
  if (tb !== null) return Math.round(tb * 1024);
  return (
    matchNumber(text, /(\d+(?:\.\d+)?)\s*gb(?:\s*(?:ssd|hdd|rom|storage))?/i) ??
    null
  );
}

function parseRamGb(text: string): number | null {
  return (
    matchNumber(text, /(\d+(?:\.\d+)?)\s*gb\s*ram/i) ??
    matchNumber(text, /ram\s*(\d+(?:\.\d+)?)\s*gb/i)
  );
}

function parseSimpleNumberByUnit(
  text: string,
  unitPattern: RegExp,
): number | null {
  return matchNumber(text, unitPattern);
}

export function deriveAttributesFromListingInputs(
  input: ListingSpecInputDraft,
): ListingSpecAttributes {
  const requirement = getRequirementsForSubcategory(input.subcategoryId);
  if (!requirement) return {};
  const sizeText = String(input.sizeText ?? "").trim();
  if (!sizeText) return {};

  const out: ListingSpecAttributes = {};
  const keys = [...requirement.required, ...requirement.optional];

  for (const key of keys) {
    switch (key) {
      case "capacity_kva":
        out[key] =
          parseSimpleNumberByUnit(sizeText, /(\d+(?:\.\d+)?)\s*kva/i) ??
          parseNumber(sizeText);
        break;
      case "horse_power":
        out[key] =
          parseSimpleNumberByUnit(
            sizeText,
            /(\d+(?:\.\d+)?)\s*(?:hp|horse\s*power)/i,
          ) ?? parseNumber(sizeText);
        break;
      case "capacity_liters":
        out[key] =
          parseSimpleNumberByUnit(
            sizeText,
            /(\d+(?:\.\d+)?)\s*(?:l|ltr|litre|liter|liters)/i,
          ) ?? parseNumber(sizeText);
        break;
      case "capacity_kg":
        out[key] =
          parseSimpleNumberByUnit(
            sizeText,
            /(\d+(?:\.\d+)?)\s*(?:kg|kilogram|kilograms)/i,
          ) ?? parseNumber(sizeText);
        break;
      case "screen_inches":
        out[key] =
          parseSimpleNumberByUnit(
            sizeText,
            /(\d+(?:\.\d+)?)\s*(?:inches|inch|in|"|\bIn\b)/i,
          ) ?? parseNumber(sizeText);
        break;
      case "dimensions_ft":
        out[key] = parseDimensionsFtText(sizeText);
        break;
      case "mattress_size":
        out[key] = parseMattressSize(sizeText);
        break;
      case "ram_gb":
        out[key] = parseRamGb(sizeText);
        break;
      case "storage_gb":
        out[key] = parseStorageGb(sizeText);
        break;
      case "burner_count":
        out[key] =
          parseSimpleNumberByUnit(sizeText, /(\d+(?:\.\d+)?)\s*burners?/i) ??
          parseNumber(sizeText);
        break;
      case "load_capacity_kg":
        out[key] =
          parseSimpleNumberByUnit(
            sizeText,
            /(\d+(?:\.\d+)?)\s*(?:kg|kilogram|kilograms)/i,
          ) ?? parseNumber(sizeText);
        break;
      default:
        break;
    }
  }

  return out;
}

function isDimensionsValue(value: unknown): value is DimensionsFtValue {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<DimensionsFtValue>;
  return (
    isFiniteNumber(candidate.length) &&
    isFiniteNumber(candidate.width) &&
    (candidate.height === undefined || isFiniteNumber(candidate.height))
  );
}

function pushIssue(
  issues: ListingSpecValidationIssue[],
  key: ListingSpecKey | "__unknown__",
  code: ListingSpecValidationIssue["code"],
  message: string,
): void {
  issues.push({ key, code, message });
}

function toErrorMap(
  issues: ListingSpecValidationIssue[],
): Partial<Record<ListingSpecKey | "__unknown__", string[]>> {
  const out: Partial<Record<ListingSpecKey | "__unknown__", string[]>> = {};
  for (const issue of issues) {
    if (!out[issue.key]) out[issue.key] = [];
    out[issue.key]?.push(issue.message);
  }
  return out;
}

function validateOneKey(
  key: ListingSpecKey,
  value: ListingSpecValue,
  requirement: SubcategorySpecRequirement,
  issues: ListingSpecValidationIssue[],
): void {
  const ui = requirement.ui[key];
  const constraint = requirement.constraints[key];
  const messageSet =
    requirement.messages[key] ?? messages(`${key} is required.`);
  if (value === null || value === undefined || value === "") return;

  if (ui?.inputType === "integer_array") {
    return;
  }

  if (ui?.inputType === "dimensions_ft") {
    if (!isDimensionsValue(value)) {
      pushIssue(
        issues,
        key,
        "invalid_dimensions",
        messageSet.invalidType ?? "Please enter valid dimensions in feet.",
      );
      return;
    }
    if (value.length <= 0 || value.width <= 0 || (value.height ?? 1) <= 0) {
      pushIssue(
        issues,
        key,
        "out_of_range",
        messageSet.outOfRange ?? "Dimensions must be greater than zero.",
      );
    }
    return;
  }

  if (ui?.inputType === "enum") {
    if (typeof value !== "string") {
      pushIssue(
        issues,
        key,
        "invalid_type",
        messageSet.invalidType ?? "Please select a valid option.",
      );
      return;
    }
    if (constraint?.enumValues && !constraint.enumValues.includes(value)) {
      pushIssue(
        issues,
        key,
        "invalid_enum",
        messageSet.invalidEnum ?? "Please choose an allowed option.",
      );
    }
    return;
  }

  if (ui?.inputType === "boolean") {
    if (typeof value !== "boolean") {
      pushIssue(
        issues,
        key,
        "invalid_type",
        messageSet.invalidType ?? "Please use a yes/no value.",
      );
    }
    return;
  }

  if (!isFiniteNumber(value)) {
    pushIssue(
      issues,
      key,
      "invalid_type",
      messageSet.invalidType ?? "Please enter a valid number.",
    );
    return;
  }

  if (constraint?.integer && !Number.isInteger(value)) {
    pushIssue(
      issues,
      key,
      "invalid_type",
      messageSet.invalidType ?? "Please enter a whole number.",
    );
  }
  if (constraint?.min !== undefined && value < constraint.min) {
    pushIssue(
      issues,
      key,
      "out_of_range",
      messageSet.outOfRange ?? `Value must be at least ${constraint.min}.`,
    );
  }
  if (constraint?.max !== undefined && value > constraint.max) {
    pushIssue(
      issues,
      key,
      "out_of_range",
      messageSet.outOfRange ?? `Value must be at most ${constraint.max}.`,
    );
  }
}

export function validateAttributesForSubcategory(
  subcategoryId: string | null | undefined,
  attributes: ListingSpecAttributes | null | undefined,
  options?: ListingSpecValidationOptions,
): ListingSpecValidationResult {
  const requirement = getRequirementsForSubcategory(subcategoryId);
  const issues: ListingSpecValidationIssue[] = [];
  if (!requirement) {
    return { isValid: true, issues, errorsByKey: {} };
  }

  const incoming = (attributes ?? {}) as Record<string, ListingSpecValue>;
  const enforceRequired = options?.enforceRequired ?? true;
  const rejectUnknown = options?.rejectUnknownKeys ?? false;
  const knownKeys = new Set<ListingSpecKey>([
    ...requirement.required,
    ...requirement.optional,
  ]);

  if (rejectUnknown) {
    for (const key of Object.keys(incoming)) {
      if (!knownKeys.has(key as ListingSpecKey)) {
        pushIssue(
          issues,
          "__unknown__",
          "unknown_key",
          `Unknown spec key '${key}' is not allowed for ${requirement.subcategoryId}.`,
        );
      }
    }
  }

  if (enforceRequired) {
    const requiredKeys = options?.enforceRequiredKeys
      ? requirement.required.filter((key) =>
          options.enforceRequiredKeys!.includes(key),
        )
      : requirement.required;
    for (const key of requiredKeys) {
      const value = incoming[key];
      if (key === "chair_seat_capacities") {
        if (!Array.isArray(value) || value.length === 0) {
          pushIssue(
            issues,
            key,
            "required",
            requirement.messages[key]?.required ??
              `${specUiMeta[key].label} is required.`,
          );
        }
        continue;
      }
      if (value === undefined || value === null || value === "") {
        pushIssue(
          issues,
          key,
          "required",
          requirement.messages[key]?.required ??
            `${specUiMeta[key].label} is required.`,
        );
      }
    }
  }

  for (const key of requirement.required) {
    validateOneKey(key, incoming[key], requirement, issues);
  }
  for (const key of requirement.optional) {
    validateOneKey(key, incoming[key], requirement, issues);
  }

  if (subcategoryId && isChairLikeSubcategory(subcategoryId)) {
    for (const issue of validateStoredChairAttributes(
      subcategoryId,
      incoming,
    )) {
      const duplicate = issues.some(
        (existing) =>
          existing.key === issue.key && existing.message === issue.message,
      );
      if (!duplicate) {
        issues.push(issue);
      }
    }
  }

  if (subcategoryId && isDiningTableChairSubcategory(subcategoryId)) {
    for (const issue of validateStoredDiningTableChairAttributes(incoming)) {
      const duplicate = issues.some(
        (existing) =>
          existing.key === issue.key && existing.message === issue.message,
      );
      if (!duplicate) {
        issues.push(issue);
      }
    }
  }

  if (subcategoryId && isFurnitureTableBundleSubcategory(subcategoryId)) {
    for (const issue of validateStoredFurnitureTableBundleAttributes(
      subcategoryId,
      incoming,
    )) {
      const duplicate = issues.some(
        (existing) =>
          existing.key === issue.key && existing.message === issue.message,
      );
      if (!duplicate) {
        issues.push(issue);
      }
    }
  }

  if (subcategoryId && isFurnitureSurfaceSubcategory(subcategoryId)) {
    for (const issue of validateStoredFurnitureSurfaceAttributes(
      subcategoryId,
      incoming,
    )) {
      const duplicate = issues.some(
        (existing) =>
          existing.key === issue.key && existing.message === issue.message,
      );
      if (!duplicate) {
        issues.push(issue);
      }
    }
  }

  // Appliance inverter family validators: full C2 contract. Skip when size-line
  // compat filter is active so live AC/washer sell keeps working until Phase 2 UI.
  const sizeLineCompat =
    options?.enforceRequiredKeys !== undefined &&
    options.enforceRequiredKeys.every((key) =>
      (SIZE_LINE_DERIVABLE_SPEC_KEYS as readonly string[]).includes(key),
    );

  if (!sizeLineCompat && subcategoryId && isAcSubcategory(subcategoryId)) {
    for (const issue of validateStoredAcAttributes(incoming)) {
      const duplicate = issues.some(
        (existing) =>
          existing.key === issue.key && existing.message === issue.message,
      );
      if (!duplicate) {
        issues.push(issue);
      }
    }
  }

  if (
    !sizeLineCompat &&
    subcategoryId &&
    isWashingMachineSubcategory(subcategoryId)
  ) {
    for (const issue of validateStoredWashingMachineAttributes(incoming)) {
      const duplicate = issues.some(
        (existing) =>
          existing.key === issue.key && existing.message === issue.message,
      );
      if (!duplicate) {
        issues.push(issue);
      }
    }
  }

  if (
    !sizeLineCompat &&
    subcategoryId &&
    isMicrowaveSubcategory(subcategoryId)
  ) {
    for (const issue of validateStoredMicrowaveAttributes(incoming)) {
      const duplicate = issues.some(
        (existing) =>
          existing.key === issue.key && existing.message === issue.message,
      );
      if (!duplicate) {
        issues.push(issue);
      }
    }
  }

  if (!sizeLineCompat && subcategoryId && isFanSubcategory(subcategoryId)) {
    for (const issue of validateStoredFanAttributes(incoming)) {
      const duplicate = issues.some(
        (existing) =>
          existing.key === issue.key && existing.message === issue.message,
      );
      if (!duplicate) {
        issues.push(issue);
      }
    }
  }

  if (
    !sizeLineCompat &&
    subcategoryId &&
    isListingFamilyMember(subcategoryId, "sewing_machine")
  ) {
    for (const issue of validateStoredSewingMachineAttributes(incoming)) {
      const duplicate = issues.some(
        (existing) =>
          existing.key === issue.key && existing.message === issue.message,
      );
      if (!duplicate) {
        issues.push(issue);
      }
    }
  }

  if (
    !sizeLineCompat &&
    subcategoryId &&
    isListingFamilyMember(subcategoryId, "play_station")
  ) {
    for (const issue of validateStoredPlayStationAttributes(incoming)) {
      const duplicate = issues.some(
        (existing) =>
          existing.key === issue.key && existing.message === issue.message,
      );
      if (!duplicate) {
        issues.push(issue);
      }
    }
  }

  if (
    !sizeLineCompat &&
    subcategoryId &&
    isGasCookerSubcategory(subcategoryId)
  ) {
    for (const issue of validateStoredGasCookerAttributes(incoming)) {
      const duplicate = issues.some(
        (existing) =>
          existing.key === issue.key && existing.message === issue.message,
      );
      if (!duplicate) {
        issues.push(issue);
      }
    }
  }

  if (!sizeLineCompat && subcategoryId && isFridgeSubcategory(subcategoryId)) {
    for (const issue of validateStoredFridgeAttributes(
      subcategoryId,
      incoming,
    )) {
      const duplicate = issues.some(
        (existing) =>
          existing.key === issue.key && existing.message === issue.message,
      );
      if (!duplicate) {
        issues.push(issue);
      }
    }
  }

  if (
    !sizeLineCompat &&
    subcategoryId &&
    isKitchenCabinetSubcategory(subcategoryId)
  ) {
    for (const issue of validateStoredKitchenCabinetAttributes(incoming)) {
      const duplicate = issues.some(
        (existing) =>
          existing.key === issue.key && existing.message === issue.message,
      );
      if (!duplicate) {
        issues.push(issue);
      }
    }
  }

  return {
    isValid: issues.length === 0,
    issues,
    errorsByKey: toErrorMap(issues),
  };
}

export function getWaveEnforcementStatus(
  subcategoryId: string | null | undefined,
  activeSubcategories: Iterable<string>,
  enabledWaves: readonly ListingSpecWave[] = LISTING_SPEC_ENABLED_WAVES,
): WaveEnforcementStatus {
  if (!subcategoryId) {
    return {
      subcategoryId: "",
      isActive: false,
      hasRequirementProfile: false,
      wave: null,
      shouldEnforce: false,
      reason: "missing_subcategory",
    };
  }

  const activeSet = new Set(activeSubcategories);
  const isActive = activeSet.has(subcategoryId);
  if (!isActive) {
    return {
      subcategoryId,
      isActive: false,
      hasRequirementProfile: false,
      wave: null,
      shouldEnforce: false,
      reason: "inactive_subcategory",
    };
  }

  const requirement = getRequirementsForSubcategory(subcategoryId);
  if (!requirement) {
    return {
      subcategoryId,
      isActive: true,
      hasRequirementProfile: false,
      wave: null,
      shouldEnforce: false,
      reason: "no_requirement_profile",
    };
  }

  const shouldEnforce = enabledWaves.includes(requirement.wave);
  return {
    subcategoryId,
    isActive: true,
    hasRequirementProfile: true,
    wave: requirement.wave,
    shouldEnforce,
    reason: shouldEnforce ? "enforced" : "wave_not_enabled",
  };
}

/**
 * Defects that block **activating** a subcategory in admin when it has
 * a non-empty required spec set. Used by `/api/admin/category-activation` preflight.
 */
export type SubcategoryActivationProfileDefect = {
  key: ListingSpecKey;
  code:
    | "missing_ui"
    | "missing_required_message"
    | "incomplete_number_constraints"
    | "incomplete_enum_constraints";
  message: string;
};

/**
 * Returns defects when the stored requirement profile is incomplete (missing
 * UI labels, messages, or constraints) for any **required** spec key. Empty when
 * the subcategory has no required keys or the profile is complete.
 */
export function getSubcategoryActivationProfileDefects(
  subcategoryId: string,
): SubcategoryActivationProfileDefect[] {
  const req = getRequirementsForSubcategory(subcategoryId);
  if (!req || req.required.length === 0) {
    return [];
  }

  const defects: SubcategoryActivationProfileDefect[] = [];
  for (const key of req.required) {
    const ui = req.ui[key];
    if (!ui || !String(ui.label ?? "").trim() || !ui.inputType) {
      defects.push({
        key,
        code: "missing_ui",
        message: `Missing or incomplete UI metadata (label or inputType) for required spec key "${key}".`,
      });
      continue;
    }
    const msg = req.messages[key];
    if (!msg || !String(msg.required ?? "").trim()) {
      defects.push({
        key,
        code: "missing_required_message",
        message: `Missing required validation message for spec key "${key}".`,
      });
      continue;
    }

    const c = req.constraints[key];
    switch (ui.inputType) {
      case "enum":
        if (!c?.enumValues || c.enumValues.length === 0) {
          defects.push({
            key,
            code: "incomplete_enum_constraints",
            message: `Enum spec for "${key}" must define a non-empty enumValues list in constraints.`,
          });
        }
        break;
      case "boolean":
        // Booleans need no min/max or enumValues in constraints.
        break;
      case "dimensions_ft":
        break;
      case "integer_array":
        break;
      case "number":
      case "integer":
        if (c === undefined || c.min === undefined || c.max === undefined) {
          defects.push({
            key,
            code: "incomplete_number_constraints",
            message: `Number spec for "${key}" must define min and max in constraints (for buyer-facing range validation).`,
          });
        }
        break;
      default: {
        const _n: never = ui.inputType;
        void _n;
        break;
      }
    }
  }
  return defects;
}
